import { TypedDocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  Sport,
  SubscribableType,
} from '@sorare/core/src/__generated__/globalTypes';
import { LoadMoreButton } from '@sorare/core/src/atoms/buttons/LoadMoreButton';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { extractConnectionData } from '@sorare/core/src/gql/extractData';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';

import { MyPage } from '../MyPage';
import { useSortByDate } from '../common/useSortByDate';
import { useSportSelect } from '../common/useSportSelect';
import { PlayerNotifications } from './PlayerNotifications';
import type {
  FollowsForNotificationsQuery,
  FollowsForNotificationsQueryVariables,
} from './__generated__/index.graphql';

const FOLLOWS_FOR_NOTIFICATIONS_QUERY = gql`
  query FollowsForNotificationsQuery(
    $cursor: String
    $subscriptionsType: [SubscribableType!]
    $sortType: SortingOption
  ) {
    currentUser {
      slug
      mySubscriptions(
        types: $subscriptionsType
        after: $cursor
        sortType: $sortType
      ) {
        nodes {
          slug
          updatedAt
          ...PlayerNotifications_subscription
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${PlayerNotifications.fragments.subscription}
` as TypedDocumentNode<
  FollowsForNotificationsQuery,
  FollowsForNotificationsQueryVariables
>;

const playersSubscriptionsType = {
  [Sport.BASEBALL]: SubscribableType.BASEBALL_PLAYER,
  [Sport.FOOTBALL]: SubscribableType.PLAYER,
  [Sport.NBA]: SubscribableType.NBA_PLAYER,
};

const Root = styled.div`
  & > * + * {
    margin-top: 15px;
  }
`;

export const PlayersNotifications = () => {
  const { sortAsVariable, SortSelect } = useSortByDate();
  const { sportInputValue, SportSelect } = useSportSelect();

  const subscriptionsTypeVaribales = useMemo(
    () => sportInputValue.map(sport => playersSubscriptionsType[sport]),
    [sportInputValue]
  );

  const { data, loading, loadMore } = usePaginatedQuery(
    FOLLOWS_FOR_NOTIFICATIONS_QUERY,
    {
      variables: {
        subscriptionsType: subscriptionsTypeVaribales,
        sortType: sortAsVariable,
      },
      connection: 'EmailSubscriptionConnection',
    }
  );

  const {
    items: follows,
    hasMore,
    cursor,
  } = extractConnectionData(data?.currentUser?.mySubscriptions, src => src);

  if (loading || !follows) return <LoadingIndicator />;

  return (
    <MyPage
      toolbar={
        <>
          <SportSelect />
          <SortSelect />
        </>
      }
    >
      <Root>
        {follows.length === 0 ? (
          <BodyM>
            <FormattedMessage
              id="PlayersNotifications.empty"
              defaultMessage="You don't follow anyone yet!"
            />
          </BodyM>
        ) : (
          <Vertical>
            {follows.map(f => (
              <PlayerNotifications key={f.slug} subscription={f} />
            ))}
          </Vertical>
        )}

        <LoadMoreButton
          hasMore={hasMore}
          loading={loading}
          loadMore={() => {
            loadMore(false, {
              cursor,
              sortType: sortAsVariable,
            });
          }}
        />
      </Root>
    </MyPage>
  );
};
